import { FC, useEffect, useRef, useState } from 'react';
import { trackButtonClick } from '@belong/analytics';
import { IResendCodeButton } from '@belong/contentful/factories/organisms/organismVerificationCodeCapture.factory';
import useAnalyticsEvents from '@belong/providers/analyticsEvents';
import { Copy } from '@belong/storybook/components/Typography';
import { ICON_NAMES } from '@belong/types';
import { FormButton } from '../../../forms/GenericFormLayout/FormButton/FormButton';
import { Icon } from '../../../Icon';
import { VisuallyHidden } from '../../../styles/Typography';
import { Stack } from '../../../ui/Stack/Stack';
import { ResendTimeoutWrapper, SecondsLeftWrapper } from './ResendCodeButton.styles';
import { RESEND_VERIFY_CODE } from './test/ResendCodeVerification.testIds';

interface IResendCodeButtonProps extends IResendCodeButton {
  resendDelayInSeconds?: number;
  showResend: boolean;
  hasError: boolean;
  onResend: () => void;
}

const RESEND_DELAY_IN_SECONDS = 60;

export const ResendCodeButton: FC<IResendCodeButtonProps> = ({
  buttonLabel,
  description,
  countDownDescription,
  resendDelayInSeconds = RESEND_DELAY_IN_SECONDS,
  showResend,
  hasError,
  onResend
}: IResendCodeButtonProps) => {
  const analyticsEvents = useAnalyticsEvents();
  const [secondsUntilResendEnabled, setSecondsUntilResendEnabled] = useState(0);
  const [hasSetTimer, setHasSetTimer] = useState<boolean>(false);
  const [showResendMessage, setShowResendMessage] = useState<boolean>(false);
  const intervalRef = useRef<number | null>(null);
  const isZero = secondsUntilResendEnabled === 0;

  const startResendTimer = () => {
    if (resendDelayInSeconds > 0) {
      setSecondsUntilResendEnabled(resendDelayInSeconds);
      setHasSetTimer(true);
      intervalRef.current = window.setInterval(resendCountDown, 1000);
    }
  };

  const resendCountDown = () => {
    setSecondsUntilResendEnabled(secondsRemaining => secondsRemaining - 1);
  };

  if (isZero && intervalRef.current) {
    window.clearInterval(intervalRef.current);
  }

  const secondsRemainingSuffix = secondsUntilResendEnabled < 2 ? 'second' : 'seconds';
  const showResendButton = showResend || isZero || hasError;
  const shouldAnnounceRemainingTime = secondsUntilResendEnabled > 0 && secondsUntilResendEnabled % 15 === 0;
  const announcementText = `${secondsUntilResendEnabled} ${secondsRemainingSuffix}`;
  useEffect(() => {
    if (hasSetTimer && isZero) {
      setHasSetTimer(false);
      setShowResendMessage(true);
    }
  }, [isZero, hasSetTimer]);

  useEffect(() => {
    let timeout: number;
    if (showResendMessage && !hasSetTimer) {
      timeout = window.setTimeout(() => setShowResendMessage(false), 3000);
    }

    return () => window.clearTimeout(timeout);
  }, [showResendMessage, hasSetTimer]);

  return (
    <Stack space={{ xs: 'small' }}>
      {showResendButton && (
        <Copy>
          <>{description}&nbsp;</>
          <FormButton
            type="button"
            data-testid={RESEND_VERIFY_CODE.VERIFICATION_CODE_RESEND}
            variant="quaternary"
            onClick={() => {
              startResendTimer();
              onResend();
              trackButtonClick({ label: buttonLabel, text: buttonLabel, analyticsEvents });
            }}
          >
            {buttonLabel}
          </FormButton>
        </Copy>
      )}
      {!showResendButton && (
        <ResendTimeoutWrapper data-testid={RESEND_VERIFY_CODE.VERIFICATION_CODE_RESEND_TIMEOUT}>
          <Icon name={ICON_NAMES.Time} />
          <Copy>
            {`${countDownDescription} `}
            <SecondsLeftWrapper>{announcementText}</SecondsLeftWrapper>
          </Copy>
        </ResendTimeoutWrapper>
      )}
      <VisuallyHidden aria-live="polite" aria-atomic="true">
        {shouldAnnounceRemainingTime && `${countDownDescription} ${announcementText}`}
      </VisuallyHidden>
    </Stack>
  );
};

ResendCodeButton.displayName = 'ResendCodeButton';
